/**
 * Financeiro → Saldo — contas financeiras do client (caixa/banco). [F2 / 017]
 *
 * Redesenho v2: a tela passa a falar a mesma língua visual da Visão geral e das
 * telas de Contas a pagar / a receber. Nada de estilo próprio — os cards de
 * indicador, painéis e gráficos usam as classes `fd-*` da dashboard; a barra de
 * filtros, as tabelas, a paginação e o drawer usam as `fc-*` de contas. O que
 * faltava para juntar as duas (grades da página, cabeçalho de cartão com tabela
 * e as pílulas entrada/saída) está em saldo.css, com os mesmos tokens.
 *
 * Estrutura:
 *   1. Filtros: período · busca · conta · tipo de movimento · Nova conta.
 *   2. Quatro indicadores: saldo disponível, entradas, saídas e resultado.
 *   3. Distribuição do saldo por conta (donut) | Contas financeiras (tabela).
 *   4. Movimentações do período (extrato) com ordenação e paginação.
 *
 * Origem dos dados: /financeiro/saldos (snapshot das contas) + as duas listas de
 * contas a pagar/receber, que já vêm inteiras. O extrato é o que foi liquidado
 * no período (data de pagamento/recebimento). Tudo é derivado aqui, então
 * trocar de filtro é instantâneo.
 *
 * "Saldo disponível" é snapshot: com uma conta selecionada mostra o saldo dela,
 * sem filtro mostra a soma das contas ativas. Ele não muda com o período — a
 * mesma exceção proposital da dashboard.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
} from 'recharts'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getSaldos, createSaldo, updateSaldo, deleteSaldo,
  getContasPagar, getContasReceber, fmtBRL,
} from '../../db/api.js'
import {
  fmtDate, PERIODO_PADRAO, iso, periodoRange, rangeAnterior, inRange,
  useFechaFora, PeriodoFiltro, listaPaginas, AccountLogo,
  TIPOS_SALDO, TIPO_SALDO_LABEL,
} from './financeiroKit.jsx'
import './financeiro-dashboard.css'
import './contas.css'
import './saldo.css'

// ── Paleta do design (a mesma da dashboard) ─────────────────────────────────
const BLUE = '#2563eb'
// cores das fatias do donut, na ordem
const CORES_CONTA = [BLUE, '#334155', '#60a5fa', '#93c5fd', '#94a3b8', '#cbd5e1']

const tooltipStyle = { borderRadius: 10, border: '1px solid #eaecf0', fontSize: 12 }

// ── Formatação ──────────────────────────────────────────────────────────────
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
// R$ sem centavos — o design mostra os indicadores arredondados (R$ 182.340)
const brl0 = (v) => `R$ ${Math.round(num(v)).toLocaleString('pt-BR')}`
const pct1 = (v) => `${num(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
// contas importadas trazem o dump da integração na descrição; só a 1ª linha
const primeiraLinha = (s) => String(s || '').split('\n')[0].trim()

const deltaPct = (atual, anterior) => (!anterior ? null : ((atual - anterior) / Math.abs(anterior)) * 100)

// ── Peças visuais ───────────────────────────────────────────────────────────

// O ⓘ do design não é decorativo: carrega a definição do indicador no `title`.
const Info = ({ texto }) => (
  <span className="fd-info" title={texto}><Icon n="info" s={13} /></span>
)

/**
 * Variação vs. período anterior. A seta segue o movimento (subiu/desceu) e a
 * cor segue o significado: em "Saídas" cair é bom, então `bomQuandoSobe={false}`
 * inverte o verde/vermelho. `valor` nulo = sem base de comparação.
 */
function Delta({ valor, bomQuandoSobe = true, suffix = 'vs. período anterior' }) {
  if (valor == null) {
    return <div className="fd-delta"><span className="fd-delta-rest">sem base de comparação</span></div>
  }
  const sobe = num(valor) >= 0
  const bom = sobe === bomQuandoSobe
  const abs = Math.abs(num(valor))
  // base quase zero no período anterior gera variação de 4 dígitos: vira teto
  const texto = abs >= 1000
    ? '>999'
    : abs.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return (
    <div className={`fd-delta ${bom ? 'up' : 'down'}`}>
      <Icon n={sobe ? 'arrow_up' : 'arrow_down'} s={13} sw={2} />
      <b>{texto}%</b>
      {suffix && <span className="fd-delta-rest">{suffix}</span>}
    </div>
  )
}

/** `nota` substitui a variação nos indicadores que são posição atual (o saldo
 *  é snapshot: não existe saldo do período anterior para comparar). */
function Kpi({ icon, label, value, delta, bomQuandoSobe = true, info, nota }) {
  return (
    <div className="fd-card fd-kpi">
      <div className="fd-kpi-head">
        <span className="fd-kpi-icon"><Icon n={icon} s={17} sw={1.7} /></span>
        <span className="fd-kpi-label">{label}</span>
        {info && <Info texto={info} />}
      </div>
      <div className="fd-kpi-value">{value}</div>
      {nota
        ? <div className="fd-delta"><span className="fd-delta-rest">{nota}</span></div>
        : <Delta valor={delta} bomQuandoSobe={bomQuandoSobe} />}
    </div>
  )
}

function PanelHead({ title, sub, info }) {
  return (
    <div className="fd-panel-head">
      <div className="fd-panel-title">{title}{sub && <span> {sub}</span>}</div>
      {info && <span className="fd-info" title={info}><Icon n="info" s={15} /></span>}
    </div>
  )
}

// Painel sem dados no período: mantém a altura do card para o grid não pular.
const SemDados = ({ altura = 150, texto = 'Sem dados no período.' }) => (
  <div className="fd-empty" style={{ height: altura }}>{texto}</div>
)

/** Cabeçalho de coluna ordenável, no padrão de Contas a pagar/receber. */
function Th({ label, k, sort, onSort, className }) {
  const ativo = sort.key === k
  return (
    <th className={className}>
      <button type="button" className="fc-th-sort" onClick={() => onSort(k)}>
        {label}
        <Icon n={ativo ? (sort.dir === 'asc' ? 'chevron_u' : 'chevron_d') : 'chevrons_up_down'}
          s={12} c={ativo ? '#344054' : '#c3c8d2'} />
      </button>
    </th>
  )
}

/**
 * Menu "..." da linha da conta. Fica em `position: fixed` ancorado no botão: a
 * tabela rola na horizontal e um menu absoluto seria cortado pelo overflow.
 */
function ContaMenu({ aberto, onToggle, onFechar, onEditar, onExcluir, busy }) {
  const ref = useFechaFora(aberto, onFechar)
  const btnRef = useRef(null)
  const [pos, setPos] = useState(null)

  useEffect(() => {
    if (!aberto) { setPos(null); return undefined }
    const ancorar = () => {
      const rect = btnRef.current?.getBoundingClientRect()
      if (!rect) return
      // abre para cima quando não há espaço abaixo (últimas linhas da página)
      const acima = window.innerHeight - rect.bottom < 160
      setPos({
        top: acima ? undefined : rect.bottom + 4,
        bottom: acima ? window.innerHeight - rect.top + 4 : undefined,
        right: window.innerWidth - rect.right,
      })
    }
    ancorar()
    window.addEventListener('resize', ancorar)
    window.addEventListener('scroll', onFechar, true)
    return () => {
      window.removeEventListener('resize', ancorar)
      window.removeEventListener('scroll', onFechar, true)
    }
  }, [aberto, onFechar])

  return (
    <div style={{ display: 'inline-block' }} ref={ref}>
      <button ref={btnRef} type="button" className="fc-icon-btn" onClick={onToggle} title="Ações" disabled={busy}>
        <Icon n="more" s={16} />
      </button>
      {aberto && pos && (
        <div className="fc-row-menu" style={pos}>
          <button type="button" onClick={() => { onFechar(); onEditar() }}>
            <Icon n="edit" s={15} /> Editar conta
          </button>
          <button type="button" className="danger" onClick={() => { onFechar(); onExcluir() }}>
            <Icon n="trash" s={15} /> Desativar conta
          </button>
        </div>
      )}
    </div>
  )
}

// ── Página ──────────────────────────────────────────────────────────────────
export default function Saldo() {
  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null

  const [contas, setContas] = useState([])
  const [pagar, setPagar] = useState([])
  const [receber, setReceber] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const [periodo, setPeriodo] = useState(PERIODO_PADRAO)
  // intervalo livre; só entra em uso quando periodo === 'custom'
  const [custom, setCustom] = useState(() => {
    const r = periodoRange(PERIODO_PADRAO)
    return { ini: iso(r.ini), fim: iso(r.fim) }
  })
  const [search, setSearch] = useState('')
  const [contaFiltro, setContaFiltro] = useState('')
  const [tipoFiltro, setTipoFiltro] = useState('')
  const [sort, setSort] = useState({ key: 'data', dir: 'desc' })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(5)
  const [menuId, setMenuId] = useState(null)
  const [drawer, setDrawer] = useState(null)   // { item } | null
  const [busy, setBusy] = useState(null)

  const load = useCallback(async () => {
    if (!clientId) { setContas([]); setPagar([]); setReceber([]); setLoading(false); return }
    setLoading(true); setErr(null)
    try {
      const [saldos, cp, cr] = await Promise.all([
        getSaldos(),
        getContasPagar().catch(() => []),
        getContasReceber().catch(() => []),
      ])
      setContas(saldos || []); setPagar(cp || []); setReceber(cr || [])
    } catch (e) { setErr(e.message) } finally { setLoading(false) }
  }, [clientId])

  useEffect(() => { setDrawer(null); load() }, [load])
  useEffect(() => { setPage(1) }, [search, contaFiltro, tipoFiltro, periodo, custom, perPage])

  const range = useMemo(() => periodoRange(periodo, custom), [periodo, custom])
  const nomeConta = useMemo(
    () => Object.fromEntries(contas.map((c) => [String(c.id), c.nome])), [contas])

  // Extrato: tudo que já foi liquidado (data de pagamento/recebimento). Uma
  // conta cancelada não movimenta caixa, mesmo com data preenchida.
  const movimentos = useMemo(() => {
    const entradas = receber
      .filter((r) => r.data_recebimento && r.status !== 'cancelada')
      .map((r) => ({
        id: `r-${r.id}`, tipo: 'entrada', data: String(r.data_recebimento).slice(0, 10),
        descricao: primeiraLinha(r.descricao), valor: num(r.valor),
        categoria: r.categoria || '', conta_id: r.conta_id ? String(r.conta_id) : '',
        parte: r.contact_nome || r.deal_titulo || '',
      }))
    const saidas = pagar
      .filter((r) => r.data_pagamento && r.status !== 'cancelada')
      .map((r) => ({
        id: `p-${r.id}`, tipo: 'saida', data: String(r.data_pagamento).slice(0, 10),
        descricao: primeiraLinha(r.descricao), valor: num(r.valor),
        categoria: r.categoria || '', conta_id: r.conta_id ? String(r.conta_id) : '',
        parte: r.fornecedor || '',
      }))
    return [...entradas, ...saidas]
  }, [pagar, receber])

  // O filtro de conta vale para os números do período e o extrato; a tabela de
  // contas e o donut continuam mostrando o quadro inteiro.
  const movsConta = useMemo(
    () => (contaFiltro ? movimentos.filter((m) => m.conta_id === contaFiltro) : movimentos),
    [movimentos, contaFiltro])

  const saldoTotal = useMemo(() => contas.reduce((s, c) => s + num(c.saldo_atual), 0), [contas])
  const saldoBase = contaFiltro
    ? num(contas.find((c) => String(c.id) === contaFiltro)?.saldo_atual)
    : saldoTotal

  // ── Indicadores ───────────────────────────────────────────────────────────
  const somaJanela = useCallback((janela) => {
    const lista = movsConta.filter((m) => inRange(m.data, janela))
    const soma = (tipo) => lista.filter((m) => m.tipo === tipo).reduce((s, m) => s + m.valor, 0)
    const entradas = soma('entrada')
    const saidas = soma('saida')
    return { entradas, saidas, resultado: entradas - saidas, qtd: lista.length }
  }, [movsConta])

  const atual = useMemo(() => somaJanela(range), [somaJanela, range])
  const anterior = useMemo(() => somaJanela(rangeAnterior(range)), [somaJanela, range])

  // Distribuição do saldo: só contas com saldo positivo entram no donut (uma
  // conta negativa não tem fatia; o texto do rodapé avisa).
  const distribuicao = useMemo(() => {
    const positivas = contas.filter((c) => num(c.saldo_atual) > 0)
    const total = positivas.reduce((s, c) => s + num(c.saldo_atual), 0)
    return {
      total,
      itens: [...positivas]
        .sort((a, b) => num(b.saldo_atual) - num(a.saldo_atual))
        .map((c) => ({ id: c.id, nome: c.nome, valor: num(c.saldo_atual), pct: total ? (num(c.saldo_atual) / total) * 100 : 0 })),
    }
  }, [contas])

  // Movimento por conta no período — colunas Entradas/Saídas da tabela.
  const porConta = useMemo(() => {
    const mapa = {}
    movimentos.filter((m) => inRange(m.data, range)).forEach((m) => {
      const k = m.conta_id || ''
      mapa[k] = mapa[k] || { entradas: 0, saidas: 0 }
      if (m.tipo === 'entrada') mapa[k].entradas += m.valor; else mapa[k].saidas += m.valor
    })
    return mapa
  }, [movimentos, range])

  // ── Extrato ───────────────────────────────────────────────────────────────
  const extrato = useMemo(() => {
    const q = search.trim().toLowerCase()
    const lista = movsConta.filter((m) => {
      if (!inRange(m.data, range)) return false
      if (tipoFiltro && m.tipo !== tipoFiltro) return false
      if (q && ![m.descricao, m.parte, m.categoria, nomeConta[m.conta_id]]
        .some((v) => (v || '').toLowerCase().includes(q))) return false
      return true
    })
    const { key, dir } = sort
    const mul = dir === 'asc' ? 1 : -1
    return [...lista].sort((a, b) => {
      if (key === 'valor') return (a.valor - b.valor) * mul
      if (key === 'conta') return String(nomeConta[a.conta_id] || '').localeCompare(String(nomeConta[b.conta_id] || '')) * mul
      return String(a[key] || '').localeCompare(String(b[key] || '')) * mul
    })
  }, [movsConta, range, tipoFiltro, search, sort, nomeConta])

  const totalPages = Math.max(1, Math.ceil(extrato.length / perPage))
  const pageAtual = Math.min(page, totalPages)
  const pageRows = extrato.slice((pageAtual - 1) * perPage, pageAtual * perPage)
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))

  // ── Mutações ──────────────────────────────────────────────────────────────
  const desativar = async (item) => {
    if (!window.confirm(`Desativar a conta “${item.nome}”? Ela deixa de contar no saldo total.`)) return
    setBusy(item.id)
    try { await deleteSaldo(item.id); await load() }
    catch (e) { alert(`Erro ao desativar: ${e.message}`) } finally { setBusy(null) }
  }

  const bc = [{ label: 'Financeiro' }, { label: 'Saldo' }]

  if (!clientId) {
    return (
      <Layout title="Saldo" breadcrumb={bc}>
        <div className="fd-state">Selecione um cliente para ver as contas financeiras.</div>
      </Layout>
    )
  }

  return (
    <Layout title="Saldo" breadcrumb={bc}>
      <div className="fs-wrap">
        {/* ── Filtros ────────────────────────────────────────────────────── */}
        <div className="fc-filters" style={{ marginBottom: 0 }}>
          <PeriodoFiltro periodo={periodo} custom={custom}
            onChange={(id, intervalo) => { if (intervalo) setCustom(intervalo); setPeriodo(id) }} />

          <div className="fc-search">
            <span className="fc-search-icon"><Icon n="search" s={15} /></span>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar no extrato por descrição, origem ou categoria…" />
          </div>

          <select className="fc-ctrl" value={contaFiltro} onChange={(e) => setContaFiltro(e.target.value)}>
            <option value="">Todas as contas</option>
            {contas.map((c) => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
          </select>

          <select className="fc-ctrl" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
            <option value="">Entradas e saídas</option>
            <option value="entrada">Só entradas</option>
            <option value="saida">Só saídas</option>
          </select>

          <div className="fc-filters-right">
            <Link className="fc-ctrl" to="/financeiro">
              <Icon n="chart" s={15} c="#667085" /> Visão geral
            </Link>
            <button type="button" className="fc-ctrl primary" onClick={() => setDrawer({ item: null })}>
              <Icon n="plus" s={15} sw={2} /> Nova conta
            </button>
          </div>
        </div>

        {err && <div className="fc-error" style={{ marginBottom: 0 }}>{err}</div>}

        {loading ? (
          <div className="fd-state">Carregando…</div>
        ) : (
          <>
            {/* ── Indicadores ───────────────────────────────────────────── */}
            <div className="fs-kpis">
              <Kpi icon="wallet" label="Saldo disponível" value={brl0(saldoBase)}
                nota={contaFiltro
                  ? TIPO_SALDO_LABEL[contas.find((c) => String(c.id) === contaFiltro)?.tipo] || 'Conta selecionada'
                  : `${contas.length} ${contas.length === 1 ? 'conta ativa' : 'contas ativas'}`}
                info="Saldo atual das contas financeiras ativas. É um snapshot: não muda com o período selecionado." />
              <Kpi icon="arrow_up_circle" label="Entradas no período" value={brl0(atual.entradas)}
                delta={deltaPct(atual.entradas, anterior.entradas)}
                info="Contas a receber efetivamente recebidas dentro do período, pela data de recebimento." />
              <Kpi icon="arrow_down_circle" label="Saídas no período" value={brl0(atual.saidas)}
                delta={deltaPct(atual.saidas, anterior.saidas)} bomQuandoSobe={false}
                info="Contas a pagar efetivamente pagas dentro do período, pela data de pagamento." />
              <Kpi icon="chart_up" label="Resultado do período" value={brl0(atual.resultado)}
                delta={deltaPct(atual.resultado, anterior.resultado)}
                info="Entradas menos saídas no período — quanto o caixa variou com o que foi liquidado." />
            </div>

            {/* ── Distribuição | Contas financeiras ─────────────────────── */}
            <div className="fs-row-split">
              <div className="fd-card fd-panel">
                <PanelHead title="Distribuição do saldo"
                  info="Peso de cada conta no saldo disponível de hoje." />
                {distribuicao.itens.length ? (
                  <>
                    <div className="fd-donut-wrap">
                      <div className="fd-donut" style={{ position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={distribuicao.itens} dataKey="valor" nameKey="nome"
                              cx="50%" cy="50%" innerRadius="68%" outerRadius="98%"
                              startAngle={90} endAngle={-270} paddingAngle={1} stroke="none"
                            >
                              {distribuicao.itens.map((c, i) => (
                                <Cell key={c.id} fill={CORES_CONTA[i % CORES_CONTA.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={tooltipStyle} />
                          </PieChart>
                        </ResponsiveContainer>
                        {/* cabe dentro do furo do donut (≈104px) sem encostar no anel */}
                        <div className="fd-donut-center">
                          <div className="fd-donut-total">{brl0(distribuicao.total)}</div>
                          <div className="fd-donut-label">Total</div>
                        </div>
                      </div>

                      <div className="fd-cat-list">
                        {distribuicao.itens.map((c, i) => (
                          <div className="fd-cat-row" key={c.id}>
                            <span className="fd-cat-name" title={c.nome}>
                              <i style={{ background: CORES_CONTA[i % CORES_CONTA.length] }} />
                              {c.nome}
                            </span>
                            <span className="fd-cat-pct">{pct1(c.pct)}</span>
                            <span className="fd-cat-val">{brl0(c.valor)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="fd-foot-note">
                      {distribuicao.total === saldoTotal
                        ? 'Saldo atual das contas ativas'
                        : `Contas com saldo positivo · saldo total ${brl0(saldoTotal)}`}
                    </div>
                  </>
                ) : <SemDados altura={190} texto="Nenhuma conta com saldo positivo." />}
              </div>

              <div className="fc-card">
                <div className="fs-card-head">
                  <div className="fd-panel-title">
                    Contas financeiras <span>{contas.length} {contas.length === 1 ? 'ativa' : 'ativas'}</span>
                  </div>
                  <span className="fs-card-head-note">Movimento no período selecionado</span>
                </div>
                {!contas.length ? (
                  <div className="fc-state">Nenhuma conta financeira cadastrada ainda.</div>
                ) : (
                  <div className="fc-table-scroll">
                    <table className="fc-table" style={{ minWidth: 780 }}>
                      <thead>
                        <tr>
                          <th>Conta</th>
                          <th>Tipo</th>
                          <th>Saldo inicial</th>
                          <th>Entradas</th>
                          <th>Saídas</th>
                          <th>Saldo atual</th>
                          <th className="fc-col-acoes">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contas.map((c) => {
                          const mov = porConta[String(c.id)] || { entradas: 0, saidas: 0 }
                          return (
                            <tr key={c.id} className={String(c.id) === contaFiltro ? 'selected' : ''}>
                              <td>
                                <div className="fs-conta-cell">
                                  <AccountLogo nome={c.nome} />
                                  <div className="fc-desc fc-cell-clip" title={c.nome}>{c.nome}</div>
                                </div>
                              </td>
                              <td>{TIPO_SALDO_LABEL[c.tipo] || c.tipo}</td>
                              <td>{fmtBRL(c.saldo_inicial || 0)}</td>
                              {/* sem movimento no período a célula fica neutra: um traço
                                  colorido de verde/vermelho leria como valor */}
                              <td className={mov.entradas ? 'fs-valor-in' : undefined}>
                                {mov.entradas ? fmtBRL(mov.entradas) : '—'}
                              </td>
                              <td className={mov.saidas ? 'fs-valor-out' : undefined}>
                                {mov.saidas ? fmtBRL(mov.saidas) : '—'}
                              </td>
                              <td className="fc-valor">{fmtBRL(c.saldo_atual || 0)}</td>
                              <td className="fc-col-acoes">
                                <ContaMenu busy={busy === c.id}
                                  aberto={menuId === c.id}
                                  onToggle={() => setMenuId((id) => (id === c.id ? null : c.id))}
                                  onFechar={() => setMenuId(null)}
                                  onEditar={() => setDrawer({ item: c })}
                                  onExcluir={() => desativar(c)} />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* ── Extrato ───────────────────────────────────────────────── */}
            <div className="fc-card">
              <div className="fs-card-head">
                <div className="fd-panel-title">
                  Movimentações do período <span>{extrato.length} {extrato.length === 1 ? 'lançamento' : 'lançamentos'}</span>
                </div>
                <span className="fs-card-head-note">
                  Entradas {fmtBRL(atual.entradas)} · Saídas {fmtBRL(atual.saidas)}
                </span>
              </div>

              {!extrato.length ? (
                <div className="fc-state">
                  {movimentos.length
                    ? 'Nenhuma movimentação corresponde aos filtros.'
                    : 'Nenhum pagamento ou recebimento liquidado ainda.'}
                </div>
              ) : (
                <>
                  <div className="fc-table-scroll">
                    <table className="fc-table" style={{ minWidth: 880 }}>
                      <thead>
                        <tr>
                          <Th label="Data" k="data" sort={sort} onSort={toggleSort} />
                          <Th label="Descrição" k="descricao" sort={sort} onSort={toggleSort} />
                          <Th label="Conta" k="conta" sort={sort} onSort={toggleSort} />
                          <th>Categoria</th>
                          <th>Tipo</th>
                          <Th label="Valor" k="valor" sort={sort} onSort={toggleSort} />
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((m) => (
                          <tr key={m.id}>
                            <td><div className="fc-date">{fmtDate(m.data)}</div></td>
                            <td>
                              <div className="fc-desc" title={m.descricao}>{m.descricao || '—'}</div>
                              {m.parte && <div className="fc-desc-sub">{m.parte}</div>}
                            </td>
                            <td className="fc-cell-clip" title={nomeConta[m.conta_id] || ''}>
                              {nomeConta[m.conta_id] || '—'}
                            </td>
                            <td className="fc-cell-clip" title={m.categoria}>{m.categoria || '—'}</td>
                            <td>
                              <span className={`fc-pill ${m.tipo === 'entrada' ? 'entrada' : 'saida'}`}>
                                {m.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                              </span>
                            </td>
                            <td className={m.tipo === 'entrada' ? 'fs-valor-in' : 'fs-valor-out'}>
                              {m.tipo === 'entrada' ? '+' : '−'} {fmtBRL(m.valor)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="fc-foot">
                    <span className="fc-foot-info">
                      Mostrando {(pageAtual - 1) * perPage + 1} a {Math.min(pageAtual * perPage, extrato.length)} de {extrato.length} registros
                    </span>
                    <div className="fc-pages">
                      <button type="button" className="fc-page" disabled={pageAtual <= 1}
                        onClick={() => setPage(pageAtual - 1)} aria-label="Página anterior">
                        <Icon n="chevron_l" s={15} />
                      </button>
                      {listaPaginas(pageAtual, totalPages).map((p, i) => (
                        p === '…'
                          ? <span key={`gap${i}`} className="fc-page-gap">…</span>
                          : (
                            <button key={p} type="button"
                              className={`fc-page${p === pageAtual ? ' active' : ''}`}
                              onClick={() => setPage(p)}>
                              {p}
                            </button>
                          )
                      ))}
                      <button type="button" className="fc-page" disabled={pageAtual >= totalPages}
                        onClick={() => setPage(pageAtual + 1)} aria-label="Próxima página">
                        <Icon n="chevron_r" s={15} />
                      </button>
                    </div>
                    <select className="fc-ctrl" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                      {[5, 10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} por página</option>)}
                    </select>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {drawer && (
        <ContaDrawer item={drawer.item}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load() }} />
      )}
    </Layout>
  )
}

// ── Drawer da conta financeira ──────────────────────────────────────────────
/**
 * Criar/editar conta. Mesmo drawer lateral das telas de Contas: cabeçalho com
 * título e fechar, corpo rolável com os campos e rodapé fixo com as ações.
 *
 * Saldo atual em branco na criação nasce igual ao inicial (regra do backend).
 */
function ContaDrawer({ item, onClose, onSaved }) {
  const isEdit = !!item
  const [f, setF] = useState(() => ({
    nome: item?.nome || '',
    tipo: item?.tipo || 'banco',
    saldo_inicial: item?.saldo_inicial != null ? String(item.saldo_inicial) : '',
    saldo_atual: item?.saldo_atual != null ? String(item.saldo_atual) : '',
  }))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))

  const salvar = async () => {
    if (saving) return
    if (!f.nome.trim()) return setErr('Informe o nome da conta.')
    if (f.saldo_inicial !== '' && isNaN(Number(f.saldo_inicial))) return setErr('Saldo inicial inválido.')
    if (f.saldo_atual !== '' && isNaN(Number(f.saldo_atual))) return setErr('Saldo atual inválido.')
    setSaving(true); setErr(null)
    const dados = {
      nome: f.nome.trim(),
      tipo: f.tipo,
      saldo_inicial: f.saldo_inicial === '' ? null : Number(f.saldo_inicial),
      saldo_atual: f.saldo_atual === '' ? null : Number(f.saldo_atual),
    }
    try {
      if (isEdit) await updateSaldo(item.id, dados)
      else await createSaldo(dados)
      onSaved()
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  return (
    <>
      <div className="fc-drawer-overlay" onMouseDown={onClose} />
      <aside className="fc-drawer" role="dialog" aria-label={isEdit ? 'Editar conta' : 'Nova conta financeira'}>
        <div className="fc-drawer-head">
          <div className="fc-drawer-title">{isEdit ? 'Editar conta' : 'Nova conta financeira'}</div>
          <button type="button" className="fc-drawer-x" onClick={onClose} aria-label="Fechar">
            <Icon n="close" s={18} />
          </button>
        </div>

        <div className="fc-drawer-body">
          {err && <div className="fc-error">{err}</div>}

          <div className="fc-field">
            <label className="fc-label">Nome da conta <i>*</i></label>
            <input className="fc-input" value={f.nome} onChange={set('nome')}
              placeholder="Ex.: Conta Corrente Itaú, Caixa" autoFocus />
            <div className="fc-hint">
              O nome define a logo exibida nas telas do Financeiro — escreva o banco por extenso.
            </div>
          </div>

          <div className="fc-field">
            <label className="fc-label">Tipo <i>*</i></label>
            <select className="fc-select" value={f.tipo} onChange={set('tipo')}>
              {TIPOS_SALDO.map((t) => <option key={t} value={t}>{TIPO_SALDO_LABEL[t]}</option>)}
            </select>
          </div>

          <div className="fc-grid-2 fc-field">
            <div className="fc-cell">
              <label className="fc-label">Saldo inicial (R$)</label>
              <input className="fc-input" type="number" step="0.01" value={f.saldo_inicial}
                onChange={set('saldo_inicial')} placeholder="0,00" />
            </div>
            <div className="fc-cell">
              <label className="fc-label">Saldo atual (R$)</label>
              <input className="fc-input" type="number" step="0.01" value={f.saldo_atual}
                onChange={set('saldo_atual')} placeholder={isEdit ? '0,00' : '= inicial se vazio'} />
              <div className="fc-hint">É o valor que entra no saldo disponível do módulo.</div>
            </div>
          </div>
        </div>

        <div className="fc-drawer-foot">
          <button type="button" className="fc-ctrl" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className="fc-ctrl primary" onClick={salvar} disabled={saving}>
            {saving ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar conta'}
          </button>
        </div>
      </aside>
    </>
  )
}
